import {
  jsonSafe,
  recordUnifiedBookingPricingAudit,
  shouldCapturePricingEngineAudit,
} from "./pricingEngineAuditStore.js";
import { resolveFlightHrCommissionAmount } from "./flightHrCommission.js";

/** @param {{ channelType?: string }} [partial] */
export function createEmptyBookingPricingModel(partial) {
  return {
    channelType: partial?.channelType ?? "unknown",
    supplierGrossAmount: null,
    supplierCurrency: null,
    supplierCommission: {
      kind: "NONE",
      percentApplied: 0,
      deductionAmount: 0,
    },
    companyNetAmount: null,
    hrCommission: { percentApplied: 0, amount: 0, tierIndex: null },
    agentCommission: {
      percentApplied: 0,
      amount: 0,
      overrideBlocked: false,
      overrideIgnored: false,
    },
    agentBaseDisplayPrice: null,
    amountAfterAgentMargin: null,
    installments: { percentApplied: 0, amount: 0 },
    preFinalChargeAmount: null,
    settlementCurrency: null,
    displayCurrency: null,
    fx: {
      fromCurrency: null,
      toCurrency: null,
      convertedAmount: null,
      fxApplied: false,
      reason: null,
    },
    displayPrice: null,
    finalChargePrice: null,
    status: "invalid_input",
    reason: null,
    auditSteps: [],
  };
}

export function roundMoney2(n) {
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 1e2) / 1e2;
}

/** @deprecated name — prefer `roundMoney2` */
export const roundHotelSearchMoney2 = roundMoney2;

const LOCKED_AGENT_CHANNELS = ["ai_chat", "agent_site"];

function isLockedChannel(ch) {
  return LOCKED_AGENT_CHANNELS.includes(ch);
}

export function resolveSupplierCommission(input) {
  const gross = Number(input.supplierGross);
  const t = String(input.commissionType ?? "PERCENT")
    .trim()
    .toUpperCase();
  if (!Number.isFinite(gross) || gross <= 0) {
    return { kind: "NONE", percentApplied: 0, deductionAmount: 0 };
  }
  if (t === "NET") {
    return { kind: "NET", percentApplied: 0, deductionAmount: 0 };
  }
  if (t === "PERCENT") {
    const p = Number(input.commissionPercent);
    const pct = Number.isFinite(p) && p >= 0 ? p : 0;
    const raw = gross * (pct / 100);
    const deduction = roundMoney2(raw) ?? raw;
    return { kind: "PERCENT", percentApplied: pct, deductionAmount: deduction };
  }
  return { kind: "NONE", percentApplied: 0, deductionAmount: 0 };
}

export function computeCompanyNet(
  supplierGross,
  supplierDeductionAmount,
  roundFn = roundMoney2
) {
  const g = Number(supplierGross);
  const d = Math.max(0, Number(supplierDeductionAmount) || 0);
  if (!Number.isFinite(g) || g <= 0) return null;
  const net = g - d;
  if (!Number.isFinite(net) || net <= 0) return null;
  const r = typeof roundFn === "function" ? roundFn(net) : roundMoney2(net);
  return r ?? net;
}

export function resolveHRCommission(input, roundFn = roundMoney2) {
  const net = Number(input.companyNet);
  if (!Number.isFinite(net) || net <= 0) {
    return { percentApplied: 0, amount: 0, tierIndex: null };
  }
  const tiers = input.hotelTiers;
  if (tiers && tiers.length > 0) {
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      const min = Number(t.minInclusive);
      const max = t.maxExclusive == null ? null : Number(t.maxExclusive);
      const okMin = net >= min;
      const okMax = max == null || net < max;
      if (okMin && okMax) {
        const pct = Number(t.percent);
        const p = Number.isFinite(pct) && pct >= 0 ? pct : 0;
        const raw = net * (p / 100);
        const rm = typeof roundFn === "function" ? roundFn : roundMoney2;
        return {
          percentApplied: p,
          amount: rm(raw) ?? raw,
          tierIndex: i,
        };
      }
    }
  }
  const fp = Number(input.flatPercent);
  const pct = Number.isFinite(fp) && fp >= 0 ? fp : 0;
  const raw = net * (pct / 100);
  const rm = typeof roundFn === "function" ? roundFn : roundMoney2;
  const percentAmount = rm(raw) ?? raw;
  const minAmount = Number(input.hrMinAmount);
  const amount =
    Number.isFinite(minAmount) && minAmount > 0
      ? rm(Math.max(percentAmount, minAmount)) ?? Math.max(percentAmount, minAmount)
      : percentAmount;
  return {
    percentApplied: pct,
    amount,
    tierIndex: null,
  };
}

export function computeAgentBaseDisplayPrice(
  companyNet,
  hrAmount,
  roundFn = roundMoney2
) {
  const a = Number(companyNet);
  const h = Number(hrAmount);
  if (!Number.isFinite(a) || a <= 0) return null;
  if (!Number.isFinite(h) || h < 0) return null;
  const sum = a + h;
  const rm = typeof roundFn === "function" ? roundFn : roundMoney2;
  return rm(sum) ?? sum;
}

export function applyAgentCommission(input, roundFn = roundMoney2) {
  const base = Number(input.baseAfterHr);
  const defRaw = Number(input.channelDefaultAgentPercent);
  const def = Number.isFinite(defRaw) && defRaw >= 0 ? defRaw : 0;
  let pct = def;
  let overrideBlocked = false;
  let overrideIgnored = false;

  const locked = isLockedChannel(input.channelType);
  const hasOverride =
    input.overrideAgentPercent != null && input.overrideAgentPercent !== "";

  if (locked) {
    if (hasOverride) overrideBlocked = true;
    pct = def;
  } else if (hasOverride) {
    const o = Number(input.overrideAgentPercent);
    if (Number.isFinite(o) && o >= 0) pct = o;
    else overrideIgnored = true;
  }

  if (!Number.isFinite(base) || base <= 0) {
    return {
      percentApplied: pct,
      amount: 0,
      overrideBlocked,
      overrideIgnored,
    };
  }
  const raw = base * (pct / 100);
  const rm = typeof roundFn === "function" ? roundFn : roundMoney2;
  return {
    percentApplied: pct,
    amount: rm(raw) ?? raw,
    overrideBlocked,
    overrideIgnored,
  };
}

export function applyInstallmentsFee(baseForFee, feePercent, roundFn = roundMoney2) {
  const b = Number(baseForFee);
  const p = Number(feePercent);
  const pct = Number.isFinite(p) && p >= 0 ? p : 0;
  if (!Number.isFinite(b) || b <= 0) {
    return { percentApplied: pct, amount: 0 };
  }
  const raw = b * (pct / 100);
  const rm = typeof roundFn === "function" ? roundFn : roundMoney2;
  return { percentApplied: pct, amount: rm(raw) ?? raw };
}

export function applyFXSettlement(input) {
  const n = Number(input.amount);
  const from = input.fromCurrency?.trim().toUpperCase() ?? null;
  const to = input.toCurrency?.trim().toUpperCase() ?? null;
  if (!Number.isFinite(n) || n <= 0) {
    return {
      fromCurrency: from,
      toCurrency: to,
      convertedAmount: null,
      fxApplied: false,
      reason: "invalid_amount",
    };
  }
  if (!from || !to) {
    return {
      fromCurrency: from,
      toCurrency: to,
      convertedAmount: null,
      fxApplied: false,
      reason: "missing_currency",
    };
  }
  if (from === to) {
    return {
      fromCurrency: from,
      toCurrency: to,
      convertedAmount: roundMoney2(n),
      fxApplied: false,
      reason: null,
    };
  }
  if (from === "USD" && to === "ILS" && typeof input.usdToIls === "function") {
    const c = input.usdToIls(n, false);
    if (c != null && Number.isFinite(Number(c)) && Number(c) > 0) {
      return {
        fromCurrency: from,
        toCurrency: to,
        convertedAmount: roundMoney2(Number(c)),
        fxApplied: true,
        reason: null,
      };
    }
  }
  if (from === "ILS" && to === "USD" && typeof input.ilsToUsd === "function") {
    const c = input.ilsToUsd(n, false);
    if (c != null && Number.isFinite(Number(c)) && Number(c) > 0) {
      return {
        fromCurrency: from,
        toCurrency: to,
        convertedAmount: roundMoney2(Number(c)),
        fxApplied: true,
        reason: null,
      };
    }
  }
  return {
    fromCurrency: from,
    toCurrency: to,
    convertedAmount: null,
    fxApplied: false,
    reason: "fx_unavailable",
  };
}

export function resolveFinalChargePrice(input) {
  const x = Number(input.preRoundAmount);
  if (!Number.isFinite(x)) return null;
  if (input.mode === "ils_integer") return Math.round(x);
  const rm =
    typeof input.roundMoney === "function" ? input.roundMoney : roundMoney2;
  return rm(x);
}

export function runUnifiedBookingPricing(rawInput) {
  const pricingAuditMeta =
    rawInput && typeof rawInput === "object"
      ? rawInput.pricingAuditMeta ?? null
      : null;
  const input =
    rawInput && typeof rawInput === "object" ? { ...rawInput } : rawInput;
  if (input && typeof input === "object") {
    delete input.pricingAuditMeta;
  }

  const finish = (result) => {
    if (shouldCapturePricingEngineAudit()) {
      try {
        recordUnifiedBookingPricingAudit({
          meta: pricingAuditMeta,
          input: jsonSafe(input),
          result: jsonSafe(result),
        });
      } catch (e) {
        console.warn("[pricing-engine-audit] record unified failed", e);
      }
    }
    return result;
  };

  const rm = typeof input.roundMoney === "function" ? input.roundMoney : roundMoney2;
  const auditSteps = [];
  const push = (s) => auditSteps.push(s);

  const chRaw = input.channelType ?? "unknown";
  push(`channel:${String(chRaw)}`);

  const model = createEmptyBookingPricingModel({
    channelType: input.channelType,
  });
  model.channelType = input.channelType;
  model.supplierCurrency = input.supplierCurrency?.trim().toUpperCase() ?? null;
  model.settlementCurrency =
    input.settlementCurrency?.trim().toUpperCase() ??
    model.supplierCurrency;
  model.displayCurrency =
    input.displayCurrency?.trim().toUpperCase() ?? model.settlementCurrency;

  const gross = Number(input.supplierGrossAmount);
  if (!Number.isFinite(gross) || gross <= 0) {
    model.status = "invalid_amount";
    model.reason = "invalid_supplier_gross";
    push("reject:invalid_supplier_gross");
    return finish({
      model,
      displayPrice: null,
      finalChargePrice: null,
      audit: { steps: auditSteps },
    });
  }
  model.supplierGrossAmount = gross;
  push("input:supplier_gross");

  const sup = resolveSupplierCommission({
    supplierGross: gross,
    commissionType: input.supplierCommissionType,
    commissionPercent: input.supplierCommissionPercent,
  });
  model.supplierCommission = sup;
  push(`supplier_commission:${sup.kind}:${sup.percentApplied}`);

  const net = computeCompanyNet(gross, sup.deductionAmount, rm);
  model.companyNetAmount = net;
  if (net == null) {
    model.status = "invalid_amount";
    model.reason = "company_net_non_positive";
    push("reject:company_net");
    return finish({
      model,
      displayPrice: null,
      finalChargePrice: null,
      audit: { steps: auditSteps },
    });
  }
  push("compute:company_net");

  const hr = resolveHRCommission(
    {
      companyNet: net,
      flatPercent: input.hrFlatPercent,
      hotelTiers: input.hotelHrTiers,
      hrMinAmount: input.hrMinAmount,
    },
    rm
  );
  model.hrCommission = hr;
  push(`hr:${hr.percentApplied}:${hr.tierIndex ?? "flat"}`);

  const baseDisplay = computeAgentBaseDisplayPrice(net, hr.amount, rm);
  model.agentBaseDisplayPrice = baseDisplay;
  if (baseDisplay == null) {
    model.status = "invalid_amount";
    model.reason = "base_display";
    push("reject:base_display");
    return finish({
      model,
      displayPrice: null,
      finalChargePrice: null,
      audit: { steps: auditSteps },
    });
  }
  push("compute:agent_base_display");

  const lockedCh = isLockedChannel(input.channelType);
  const hasAgentOverride =
    input.overrideAgentPercent != null && input.overrideAgentPercent !== "";

  const agent = applyAgentCommission(
    {
      channelType: input.channelType,
      baseAfterHr: baseDisplay,
      channelDefaultAgentPercent: input.channelDefaultAgentPercent ?? 0,
      overrideAgentPercent: input.overrideAgentPercent,
    },
    rm
  );
  model.agentCommission = agent;
  push(
    `agent_commission:${agent.percentApplied}${agent.overrideBlocked ? ":override_blocked" : ""}`
  );

  if (lockedCh) {
    push("locked_channel:rule_applied");
    if (agent.overrideBlocked) {
      push("locked_channel:manual_override_blocked");
    }
  }
  if (agent.overrideBlocked) {
    push("agent_commission_source:channel_default_override_ignored");
  } else if (hasAgentOverride && !lockedCh) {
    const o = Number(input.overrideAgentPercent);
    if (Number.isFinite(o) && o >= 0) {
      push("agent_commission_source:manual_override");
    } else {
      push("agent_commission_source:channel_default");
    }
  } else {
    push("agent_commission_source:channel_default");
  }

  const afterAgent = rm(baseDisplay + agent.amount);
  model.amountAfterAgentMargin = afterAgent;
  push("compute:after_agent");

  const inst = applyInstallmentsFee(
    afterAgent ?? baseDisplay,
    input.installmentsFeePercent ?? 0,
    rm
  );
  model.installments = inst;
  const preFinal =
    rm((afterAgent ?? baseDisplay) + inst.amount) ??
    (afterAgent ?? baseDisplay) + inst.amount;
  model.preFinalChargeAmount = preFinal;
  push("compute:pre_final");

  const settleCur = model.settlementCurrency;
  const dispCur = model.displayCurrency;
  let displayNum = preFinal;
  if (settleCur && dispCur && settleCur !== dispCur) {
    const fx = applyFXSettlement({
      amount: preFinal,
      fromCurrency: settleCur,
      toCurrency: dispCur,
      usdToIls: input.usdToIls,
      ilsToUsd: input.ilsToUsd,
    });
    model.fx = {
      fromCurrency: fx.fromCurrency,
      toCurrency: fx.toCurrency,
      convertedAmount: fx.convertedAmount,
      fxApplied: fx.fxApplied,
      reason: fx.reason,
    };
    if (fx.convertedAmount == null) {
      model.status = "fx_unavailable";
      model.reason = fx.reason;
      push("reject:fx");
      return finish({
        model,
        displayPrice: null,
        finalChargePrice: null,
        audit: { steps: auditSteps },
      });
    }
    displayNum = fx.convertedAmount;
    push("fx:settlement_to_display");
  } else {
    model.fx = {
      fromCurrency: settleCur,
      toCurrency: dispCur,
      convertedAmount: preFinal,
      fxApplied: false,
      reason: null,
    };
    push("fx:no_conversion");
  }

  model.displayPrice = displayNum;
  const finalMode =
    input.hotelFinalChargeInteger && (settleCur === "ILS" || dispCur === "ILS")
      ? "ils_integer"
      : "money2";
  model.finalChargePrice = resolveFinalChargePrice({
    preRoundAmount: preFinal,
    mode: finalMode,
    roundMoney: rm,
  });
  push(`final_charge:${finalMode}`);

  model.status = "ok";
  model.reason = null;
  model.auditSteps = auditSteps;

  return finish({
    model,
    displayPrice: model.displayPrice,
    finalChargePrice: model.finalChargePrice,
    audit: { steps: auditSteps },
  });
}

/** Agent hotel customer-facing HR — percent base + ILS min/max clamp (hotels only). */
export const AGENT_HOTEL_HR_COMMISSION_DEFAULT_PERCENT = 4.5;
export const AGENT_HOTEL_HR_COMMISSION_MIN_ILS = 250;
export const AGENT_HOTEL_HR_COMMISSION_MAX_ILS = 600;

/**
 * HR commission ILS = min(max(supplierPriceIls * pct/100, MIN), MAX)
 * @param {number} supplierPriceIls
 * @param {number} [hotelHrCommissionPercent]
 * @returns {number|null}
 */
export function computeAgentHotelHrCommissionIls(
  supplierPriceIls,
  hotelHrCommissionPercent = AGENT_HOTEL_HR_COMMISSION_DEFAULT_PERCENT
) {
  const base = Number(supplierPriceIls);
  if (!Number.isFinite(base) || base <= 0) return null;
  const pct = Number(hotelHrCommissionPercent);
  const p = Number.isFinite(pct) && pct >= 0 ? pct : AGENT_HOTEL_HR_COMMISSION_DEFAULT_PERCENT;
  if (p === 0) return 0;
  const pctAmount = base * (p / 100);
  return roundMoney2(
    Math.min(
      Math.max(pctAmount, AGENT_HOTEL_HR_COMMISSION_MIN_ILS),
      AGENT_HOTEL_HR_COMMISSION_MAX_ILS
    )
  );
}

function convertAgentHotelCommissionIlsToFlowCurrency(
  commissionIls,
  flowCurrency,
  usdToIls,
  ilsToUsd
) {
  const flow = String(flowCurrency ?? "ILS")
    .trim()
    .toUpperCase();
  if (commissionIls == null || !Number.isFinite(Number(commissionIls))) return null;
  if (flow === "ILS") return roundMoney2(Number(commissionIls));
  if (flow === "USD") {
    const fx = applyFXSettlement({
      amount: Number(commissionIls),
      fromCurrency: "ILS",
      toCurrency: "USD",
      usdToIls,
      ilsToUsd,
    });
    return fx?.convertedAmount != null ? roundMoney2(Number(fx.convertedAmount)) : null;
  }
  return null;
}

export function runAgentHotelHrMarkup(input) {
  const base = Number(input.basePrice);
  const pct = Number(input.hotelHrCommissionPercent);
  const flowCurrency = String(input.baseCurrency ?? input.displayCurrency ?? "ILS")
    .trim()
    .toUpperCase();
  const supplierPriceIls = Number(input.supplierPriceIls);
  const sup = resolveSupplierCommission({
    supplierGross: base,
    commissionType: "NET",
  });
  const net = computeCompanyNet(base, sup.deductionAmount);
  const safeNet = net ?? (Number.isFinite(base) && base > 0 ? base : null);
  if (safeNet == null) {
    return {
      companyNet: null,
      hr: { percentApplied: 0, amount: 0, commissionIls: 0, tierIndex: null },
      finalPrice: null,
      commissionAmount: null,
      commissionIls: null,
    };
  }
  const p = Number.isFinite(pct) && pct >= 0 ? pct : 0;
  if (p === 0) {
    return {
      companyNet: safeNet,
      hr: { percentApplied: 0, amount: 0, commissionIls: 0, tierIndex: null },
      finalPrice: safeNet,
      commissionAmount: 0,
      commissionIls: 0,
    };
  }
  if (!Number.isFinite(supplierPriceIls) || supplierPriceIls <= 0) {
    return {
      companyNet: safeNet,
      hr: { percentApplied: p, amount: 0, commissionIls: null, tierIndex: null },
      finalPrice: null,
      commissionAmount: null,
      commissionIls: null,
      pricingStatus: "missing_supplier_ils_for_hr",
    };
  }
  const commissionIls = computeAgentHotelHrCommissionIls(supplierPriceIls, p);
  const commissionAmount = convertAgentHotelCommissionIlsToFlowCurrency(
    commissionIls,
    flowCurrency,
    input.usdToIls,
    input.ilsToUsd
  );
  if (commissionAmount == null) {
    return {
      companyNet: safeNet,
      hr: { percentApplied: p, amount: 0, commissionIls, tierIndex: null },
      finalPrice: null,
      commissionAmount: null,
      commissionIls,
      pricingStatus: "missing_fx_for_hr_commission",
    };
  }
  const finalPrice = resolveFinalChargePrice({
    preRoundAmount: safeNet + commissionAmount,
    mode: "money2",
  });
  const hr = {
    percentApplied: p,
    amount: commissionAmount ?? 0,
    commissionIls: commissionIls ?? 0,
    tierIndex: null,
  };
  return {
    companyNet: safeNet,
    hr,
    finalPrice,
    commissionAmount: commissionAmount ?? 0,
    commissionIls: commissionIls ?? 0,
  };
}

/** תואם ל־`round2` ב־AppContext (כרגע Math.ceil). */
export function legacyAppRound2(num) {
  const n = parseFloat(num);
  return Math.ceil(n);
}

/**
 * זנב תמחור חבילה ב-ILS אחרי שכבת השוליים המאוחדת (סכום כבר מחושב):
 * priceWithCommission → עמלת אשראי → מחיר סופי.
 * תואם לענף הלא-hotelFinalRoundOnly ב־runAppContextBaseCommissionPricing (legacyAppRound2).
 *
 * @param {number} initialPriceIls — initialPrice / company net ב-ILS לפני שוליים
 * @param {number} unifiedMarginAmountIls — סכום שוליים מאוחד להוספה (אחוז או קבוע כבר הומר לסכום)
 * @param {() => number} getCreditCardFeePercent
 * @returns {{ priceWithCommission: number, creditCardFee: number, priceWithCommissionAndCreditCardFee: number }|null}
 */
export function runPackageIlsUnifiedMarginAndCardTail(
  initialPriceIls,
  unifiedMarginAmountIls,
  getCreditCardFeePercent
) {
  const initial = Number(initialPriceIls);
  const margin = Number(unifiedMarginAmountIls);
  if (!Number.isFinite(initial) || initial <= 0) return null;
  if (!Number.isFinite(margin) || margin < 0) return null;
  const getCc =
    typeof getCreditCardFeePercent === "function"
      ? getCreditCardFeePercent
      : () => 0;
  const priceWithCommission = legacyAppRound2(initial + margin);
  const ccBase = parseFloat(String(priceWithCommission));
  const creditCardFee = legacyAppRound2((ccBase * getCc()) / 100);
  const priceWithCommissionAndCreditCardFee = legacyAppRound2(
    priceWithCommission + creditCardFee
  );
  return {
    priceWithCommission,
    creditCardFee,
    priceWithCommissionAndCreditCardFee,
  };
}

/**
 * מסלול `calcBaseCommission` היסטורי — כל הלוגיקה כאן; AppContext רק קורא.
 * @param {object} p
 * @param {number|string} p.price — בסיס ILS (או מספר שמומר ל־ILS לפני הקריאה)
 * @param {number|null|undefined} [p.hrCommissionPercent]
 * @param {number|null|undefined} [p.agentCommissionPercent]
 * @param {boolean} [p.isHotel]
 * @param {boolean} [p.hotelFinalRoundOnly]
 * @param {boolean} [p.isB2CMode]
 * @param {() => number} p.getHrCommissionPercent
 * @param {() => number} p.getHrCommissionPercentHotels
 * @param {() => number} p.getAgentCommissionPercent
 * @param {() => number} p.getCreditCardFeePercent
 * @param {number|null|undefined} [p.entitlementAgentPercent] — legacy; לא בשימוש ל-unified HR (רק wallet — לא מועבר יותר מ-calcBaseCommission)
 * @param {number|null|undefined} [p.hrMarginPercentOverride] — ממטריצת HR לפי קטגוריה; דוחף selectedHrPercent
 * @param {string|null|undefined} [p.hrMarginSourceFeatureKey] — מפתח פיצ'ר (trace)
 * @param {number|null|undefined} [p.walletCommissionPercent] — commission_percentage (ארנק בלבד, trace)
 * @returns {{ status: 'ok'|'missing_price', ourPrice: object|null, auditSteps: string[] }}
 */
export function runAppContextBaseCommissionPricing(p) {
  const auditSteps = ["app_commission:enter"];
  console.log("[HR_MARGIN_SOURCE_TRACE]", "runAppContextBaseCommissionPricing enter", {
    hrMarginSourceFeatureKey: p.hrMarginSourceFeatureKey,
    hrMarginPercentOverride: p.hrMarginPercentOverride,
  });
  const raw = parseFloat(p.price);
  if (!Number.isFinite(raw) || raw <= 0) {
    auditSteps.push("reject:invalid_base_price");
    return { status: "missing_price", ourPrice: null, auditSteps };
  }

  const isHotel = Boolean(p.isHotel);
  const hotelFinalRoundOnly = Boolean(p.hotelFinalRoundOnly);
  const initialPrice = hotelFinalRoundOnly ? raw : legacyAppRound2(raw);
  auditSteps.push(
    `base:initial:${initialPrice}:hotelFinalRoundOnly:${hotelFinalRoundOnly}`
  );

  let hrCommissionPercent = p.hrCommissionPercent;
  if (!hrCommissionPercent) {
    if (p.isB2CMode) hrCommissionPercent = 15;
    else if (isHotel) hrCommissionPercent = p.getHrCommissionPercentHotels();
    else hrCommissionPercent = p.getHrCommissionPercent();
  }

  let agentCommissionPercent = p.agentCommissionPercent;
  if (!agentCommissionPercent && agentCommissionPercent !== 0) {
    agentCommissionPercent = p.getAgentCommissionPercent();
  }

  const ourPrice = { initialPrice };
  ourPrice.hrCommissionPercent = hrCommissionPercent;
  ourPrice.agentCommissionPercent = agentCommissionPercent;

  const hrOv = p.hrMarginPercentOverride;
  const selectedHrPercent =
    hrOv != null && Number.isFinite(Number(hrOv))
      ? Number(hrOv)
      : Number(agentCommissionPercent) > 0
        ? Number(agentCommissionPercent)
        : Number(hrCommissionPercent);

  ourPrice.unifiedHrMarginSourceFeatureKey =
    p.hrMarginSourceFeatureKey != null && String(p.hrMarginSourceFeatureKey).trim() !== ""
      ? String(p.hrMarginSourceFeatureKey)
      : null;
  ourPrice.walletCommissionPercent =
    p.walletCommissionPercent != null && Number.isFinite(Number(p.walletCommissionPercent))
      ? Number(p.walletCommissionPercent)
      : null;

  console.log("[HR_MARGIN_SOURCE_TRACE]", "runAppContextBaseCommissionPricing ourPrice set", {
    unifiedHrMarginSourceFeatureKey: ourPrice.unifiedHrMarginSourceFeatureKey,
    input_hrMarginSourceFeatureKey: p.hrMarginSourceFeatureKey,
  });

  const hrShadowAmount = hotelFinalRoundOnly
    ? (hrCommissionPercent / 100) * initialPrice
    : legacyAppRound2((hrCommissionPercent / 100) * initialPrice);
  let unifiedHrMargin = hotelFinalRoundOnly
    ? (selectedHrPercent / 100) * initialPrice
    : legacyAppRound2((selectedHrPercent / 100) * initialPrice);
  if (p.applyFlightHrMinFloor === true) {
    const hrBeforeFlightFloor = unifiedHrMargin;
    unifiedHrMargin = resolveFlightHrCommissionAmount({
      percentAmount: unifiedHrMargin,
      passengersCount: p.passengersCount,
      directionCount: p.flightHrDirectionCount,
      usdToCurrencyRate: p.flightHrMinUsdToCurrencyRate,
      roundFn: hotelFinalRoundOnly ? undefined : legacyAppRound2,
    });
    if (unifiedHrMargin !== hrBeforeFlightFloor) {
      auditSteps.push(
        `flight_hr_min_floor:${hrBeforeFlightFloor}->${unifiedHrMargin}`
      );
    }
  }
  const unifiedToAdd = unifiedHrMargin > 0 ? unifiedHrMargin : 0;

  ourPrice.handlingFee = 0;
  ourPrice.unifiedHrMarginPercent = selectedHrPercent;
  ourPrice.unifiedHrMargin = unifiedHrMargin;
  // Must match margin baked into priceWithCommission (selected/override %), not legacy shadow %.
  ourPrice.hrCommission = unifiedToAdd;
  ourPrice.agentCommission = unifiedHrMargin;
  if (
    Number.isFinite(hrShadowAmount) &&
    Math.abs(hrShadowAmount - unifiedToAdd) > 0.02
  ) {
    auditSteps.push(
      `hr_shadow_legacy:${hrShadowAmount}:applied_hr:${unifiedToAdd}`
    );
  }

  auditSteps.push(
    `hr_pct:${hrCommissionPercent}:agent_pct:${agentCommissionPercent}:selected:${selectedHrPercent}`
  );

  if (hotelFinalRoundOnly) {
    ourPrice.priceWithCommission = initialPrice + unifiedToAdd;
    const ccPct = p.getCreditCardFeePercent();
    ourPrice.creditCardFee = (ourPrice.priceWithCommission * ccPct) / 100;
    const preFinal = ourPrice.priceWithCommission + ourPrice.creditCardFee;
    ourPrice.pricePreFinalRound = preFinal;
    ourPrice.priceWithCommissionAndCreditCardFee = Math.round(preFinal);
    auditSteps.push(
      `branch:hotelFinalRoundOnly:preFinal:${preFinal}:final:${ourPrice.priceWithCommissionAndCreditCardFee}`
    );
    auditSteps.push("app_commission:ok");
    return { status: "ok", ourPrice, auditSteps };
  }

  ourPrice.priceWithCommission = legacyAppRound2(initialPrice + unifiedToAdd);
  const ccBase = parseFloat(ourPrice.priceWithCommission);
  ourPrice.creditCardFee = legacyAppRound2(
    (ccBase * p.getCreditCardFeePercent()) / 100
  );
  ourPrice.priceWithCommissionAndCreditCardFee = legacyAppRound2(
    ourPrice.priceWithCommission + ourPrice.creditCardFee
  );
  auditSteps.push(
    `branch:default:pwcc:${ourPrice.priceWithCommissionAndCreditCardFee}`
  );
  auditSteps.push("app_commission:ok");
  return { status: "ok", ourPrice, auditSteps };
}

/**
 * תצוגת מחיר מכירה מלון: USD ספק → ILS → אותו מסלול commission כמו hotelFinalRoundOnly.
 * @param {object} p
 * @param {number} p.supplierUsd
 * @param {(n: number, fixed?: boolean) => number} p.usdToIls
 * @param {(n: number, fixed?: boolean) => number} [p.ilsToUsd]
 * @param {number} [p.linkedFlightsDealPrice]
 * @param {object} p.commissionContext — אובייקט מעבור ל־runAppContextBaseCommissionPricing (ללא price)
 * @param {string} [p.context]
 * @param {string|null} [p.priceSource]
 * @param {boolean} [p.fallbackUsed]
 */
export function runHotelSellingDisplayFromEngine(p) {
  const auditSteps = ["hotel_selling_display:enter"];
  const n = Number(p.supplierUsd);
  const linked = Number(p.linkedFlightsDealPrice) || 0;

  if (!Number.isFinite(n) || n <= 0) {
    auditSteps.push("reject:invalid_supplier_usd");
    return {
      sourcePrice: null,
      sourceCurrency: "USD",
      finalDisplayPrice: null,
      finalDisplayPriceUsd: null,
      commissionApplied: null,
      labelKey: p.fallbackUsed
        ? "hotelPricing.searchEstimateHint"
        : "hotelPricing.sellingPriceLine",
      metadata: {
        context: p.context ?? "unknown",
        priceSource: p.priceSource ?? null,
        fallbackUsed: Boolean(p.fallbackUsed),
        hasUsd: false,
      },
      pricingStatus: "missing_price",
      auditSteps,
    };
  }

  if (typeof p.usdToIls !== "function") {
    auditSteps.push("reject:missing_usdToIls");
    return {
      sourcePrice: n,
      sourceCurrency: "USD",
      finalDisplayPrice: null,
      finalDisplayPriceUsd: null,
      commissionApplied: null,
      labelKey: p.fallbackUsed
        ? "hotelPricing.searchEstimateHint"
        : "hotelPricing.sellingPriceLine",
      metadata: {
        context: p.context ?? "unknown",
        priceSource: p.priceSource ?? null,
        fallbackUsed: Boolean(p.fallbackUsed),
        hasUsd: true,
      },
      pricingStatus: "missing_price",
      auditSteps,
    };
  }

  const baseIls = p.usdToIls(n, false);
  auditSteps.push(`fx:usd_to_ils:${baseIls}`);

  const cr = runAppContextBaseCommissionPricing({
    ...p.commissionContext,
    price: baseIls,
    isHotel: true,
    hotelFinalRoundOnly: true,
  });

  for (let i = 0; i < cr.auditSteps.length; i++) {
    auditSteps.push(`nested:${cr.auditSteps[i]}`);
  }

  if (cr.status !== "ok" || !cr.ourPrice) {
    auditSteps.push("reject:commission_path");
    return {
      sourcePrice: n,
      sourceCurrency: "USD",
      finalDisplayPrice: null,
      finalDisplayPriceUsd: null,
      commissionApplied: null,
      labelKey: p.fallbackUsed
        ? "hotelPricing.searchEstimateHint"
        : "hotelPricing.sellingPriceLine",
      metadata: {
        context: p.context ?? "unknown",
        priceSource: p.priceSource ?? null,
        fallbackUsed: Boolean(p.fallbackUsed),
        hasUsd: true,
      },
      pricingStatus: "missing_price",
      auditSteps,
    };
  }

  const finalDisplayPrice =
    cr.ourPrice.priceWithCommissionAndCreditCardFee + linked;
  const finalDisplayPriceUsd =
    finalDisplayPrice != null && typeof p.ilsToUsd === "function"
      ? p.ilsToUsd(finalDisplayPrice)
      : null;

  auditSteps.push(`add:linked_flights:${linked}`);
  auditSteps.push("hotel_selling_display:ok");

  return {
    sourcePrice: n,
    sourceCurrency: "USD",
    finalDisplayPrice,
    finalDisplayPriceUsd,
    commissionApplied: cr.ourPrice,
    labelKey: p.fallbackUsed
      ? "hotelPricing.searchEstimateHint"
      : "hotelPricing.sellingPriceLine",
    metadata: {
      context: p.context ?? "unknown",
      priceSource: p.priceSource ?? null,
      fallbackUsed: Boolean(p.fallbackUsed),
      hasUsd: true,
    },
    pricingStatus: "ok",
    auditSteps,
  };
}
